export const SIM_TICK_RATE = 120;
export const SIM_DT = 1 / SIM_TICK_RATE;

// DynamiX inherits RocketSim's gameplay state in Unreal Units (uu), but its collision world
// uses Bullet units at 50 uu per simulation unit. The CMF files are already in
// that Bullet scale, so all dynamic bodies must use the same conversion.
export const UNREAL_UNITS_PER_SIM_UNIT = 50;
export const SIM_UNIT_METERS = UNREAL_UNITS_PER_SIM_UNIT / 100;

export const uuToSim = (value: number): number => value / UNREAL_UNITS_PER_SIM_UNIT;
export const simToUu = (value: number): number => value * UNREAL_UNITS_PER_SIM_UNIT;

export const ARENA = Object.freeze({
  width: uuToSim(4096 * 2),
  length: uuToSim(5120 * 2),
  height: uuToSim(2048),
});

export const BALL = Object.freeze({
  radius: uuToSim(91.25),
  restingHeight: uuToSim(93.15),
});

export const SOCCAR_GOAL = Object.freeze({
  scoreBaseZ: uuToSim(5124.25),
});

export const CAR = Object.freeze({
  restingHeight: uuToSim(17),
  // Octane hitbox mapped from DynamiX forward/right/up into game x/y/z.
  halfExtents: {
    x: uuToSim(86.6994 / 2),
    y: uuToSim(38.6591 / 2),
    z: uuToSim(120.507 / 2),
  },
});

const SMALL_BOOST_AMOUNT = 12;
const SMALL_BOOST_RESPAWN_SECONDS = 4;
const BIG_BOOST_AMOUNT = 100;
const BIG_BOOST_RESPAWN_SECONDS = 10;
const SMALL_BOOST_CENTER_HEIGHT_UU = 70;
const BIG_BOOST_CENTER_HEIGHT_UU = 73;
const SMALL_BOOST_PICKUP_RADIUS_UU = 144;
const BIG_BOOST_PICKUP_RADIUS_UU = 208;
const BOOST_PICKUP_HALF_HEIGHT_UU = 95;
const smallBoostPads = [
  [0, -4240],
  [-1792, -4184],
  [1792, -4184],
  [-940, -3308],
  [940, -3308],
  [0, -2816],
  [-3584, -2484],
  [3584, -2484],
  [-1788, -2300],
  [1788, -2300],
  [-2048, -1036],
  [0, -1024],
  [2048, -1036],
  [-1024, 0],
  [1024, 0],
  [-2048, 1036],
  [0, 1024],
  [2048, 1036],
  [-1788, 2300],
  [1788, 2300],
  [-3584, 2484],
  [3584, 2484],
  [0, 2816],
  [-940, 3308],
  [940, 3308],
  [-1792, 4184],
  [1792, 4184],
  [0, 4240],
] as const;

const bigBoostPads = [
  [-3584, 0],
  [3584, 0],
  [-3072, 4096],
  [3072, 4096],
  [-3072, -4096],
  [3072, -4096],
] as const;

export const BOOST_PADS = Object.freeze([
  ...smallBoostPads.map(([x, z]) => ({
    x: uuToSim(x),
    y: uuToSim(SMALL_BOOST_CENTER_HEIGHT_UU),
    z: uuToSim(z),
    amount: SMALL_BOOST_AMOUNT,
    respawn: SMALL_BOOST_RESPAWN_SECONDS,
    isBig: false,
    pickupRadius: uuToSim(SMALL_BOOST_PICKUP_RADIUS_UU),
    pickupHalfHeight: uuToSim(BOOST_PICKUP_HALF_HEIGHT_UU),
  })),
  ...bigBoostPads.map(([x, z]) => ({
    x: uuToSim(x),
    y: uuToSim(BIG_BOOST_CENTER_HEIGHT_UU),
    z: uuToSim(z),
    amount: BIG_BOOST_AMOUNT,
    respawn: BIG_BOOST_RESPAWN_SECONDS,
    isBig: true,
    pickupRadius: uuToSim(BIG_BOOST_PICKUP_RADIUS_UU),
    pickupHalfHeight: uuToSim(BOOST_PICKUP_HALF_HEIGHT_UU),
  })),
] as const);
